import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, FilePlus, Loader2, Trash2 } from 'lucide-react';
import { InvoiceCostCenter } from '../../types';
import {
    createInvoiceCostCenter,
    deleteInvoiceCostCenter,
} from '../../services/invoiceCostCenters';
import { describeError } from '../../services/errorMessage';

/**
 * Aufklappmenue der gespeicherten BEZEICHNUNGEN neben dem Bezeichnungsfeld der
 * Kostenstelle im Rechnungskopf.
 *
 * Was hier gewaehlt wird, ist der Text LINKS auf dem Beleg ("Kostenstelle",
 * "PSP-Element", "Innenauftrag") — nicht der Wert dahinter. Der ist je Beleg
 * verschieden und waere als Vorlage wertlos; wiederkehrend ist nur, wie der
 * Auftraggeber die Angabe nennt. Getippt werden muss der Wert trotzdem selten:
 * ein neuer Entwurf uebernimmt die zuletzt verwendete Kombination.
 *
 * Aufbau und Optik nach TextTemplatePicker (crm/), aber eigenstaendig: die
 * dortige Vorlage haengt an einer Kategorie und am Angebotseditor, der hier
 * nichts zu verlieren hat.
 *
 * Der Service-Aufruf liegt in dieser Komponente, die Liste haelt der Editor:
 * dasselbe Verhaeltnis wie beim Vorbild.
 */

interface Props {
    /** Gespeicherte Bezeichnungen, vom Editor geladen und gehalten. */
    templates: InvoiceCostCenter[];
    /**
     * Das Laden ist fehlgeschlagen — aus einem anderen Grund als "Migration
     * noch nicht gefahren" (der ist angekuendigt und bleibt still).
     *
     * Ohne diesen Hinweis sieht ein Rechte- oder Netzfehler genauso aus wie
     * "es gibt eben noch keine Vorlagen", und der Bearbeiter sucht den Fehler
     * bei sich. Dezent, nicht alarmierend: die Kostenstelle laesst sich immer
     * von Hand eintippen, es geht nichts verloren.
     */
    unavailable?: boolean;
    /** Aktuelle Bezeichnung im Feld — das, was "Als Vorlage speichern" sichert. */
    currentLabel: string;
    disabled?: boolean;
    onPick: (label: string) => void;
    onCreated: (t: InvoiceCostCenter) => void;
    onDeleted: (id: string) => void;
}

const UNAVAILABLE_NOTE = 'Vorlagen derzeit nicht verfügbar';

export const CostCenterPicker: React.FC<Props> = ({
    templates,
    unavailable,
    currentLabel,
    disabled,
    onPick,
    onCreated,
    onDeleted,
}) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    // Klick ausserhalb schliesst das Aufklappmenue (Muster TextTemplatePicker).
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const handlePick = (label: string) => {
        // Ohne Rueckfrage ersetzen, anders als beim Textbaustein: hier steht
        // ein kurzes Wort im Feld, kein selbst geschriebener Absatz — eine
        // Sicherheitsfrage waere hier nur im Weg. Der WERT daneben bleibt
        // unangetastet: wer die Bezeichnung wechselt, meint nicht die Nummer.
        onPick(label);
        setOpen(false);
    };

    const handleDelete = async (id: string) => {
        if (busy) return;
        setBusy(true);
        try {
            await deleteInvoiceCostCenter(id);
            onDeleted(id);
        } catch (e) {
            alert('Vorlage konnte nicht gelöscht werden: ' + describeError(e));
        } finally {
            setBusy(false);
        }
    };

    const handleSaveCurrent = async () => {
        // Kein prompt() mehr: gespeichert wird genau das Wort, das im Feld
        // steht. Eine Rueckfrage, die dasselbe noch einmal erfragt, waere ein
        // Klick ohne Entscheidung.
        const name = currentLabel.trim();
        if (!name) return;
        setBusy(true);
        try {
            const created = await createInvoiceCostCenter({ name });
            onCreated(created);
            setOpen(false);
        } catch (e) {
            // Bewusst sichtbar: anders als beim Laden darf ein fehlgeschlagenes
            // Speichern nicht still bleiben — sonst haelt der Bearbeiter die
            // Vorlage fuer angelegt.
            alert('Vorlage konnte nicht gespeichert werden: ' + describeError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div ref={wrapRef} className="relative shrink-0">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                disabled={disabled}
                className="flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                title={unavailable ? UNAVAILABLE_NOTE : 'Gespeicherte Bezeichnungen'}
            >
                Vorlagen
                <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {/* Unter dem Knopf statt als Meldung: der Hinweis erklaert eine
                leere Liste, er unterbricht nicht die Arbeit. */}
            {unavailable && (
                <span className="mt-1 block text-right text-[11px] leading-tight text-amber-600">
                    {UNAVAILABLE_NOTE}
                </span>
            )}
            {open && (
                <div className="absolute right-0 z-50 mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
                    <div className="max-h-56 overflow-y-auto py-1">
                        {templates.length === 0 ? (
                            <div className="px-4 py-4 text-center text-xs text-slate-400">
                                {unavailable
                                    ? `${UNAVAILABLE_NOTE}. Bezeichnung bitte von Hand eintragen.`
                                    : 'Noch keine Bezeichnungen gespeichert.'}
                            </div>
                        ) : (
                            templates.map((t) => (
                                <div
                                    key={t.id}
                                    className="flex items-center gap-2 px-2 hover:bg-blue-50/60"
                                >
                                    <button
                                        type="button"
                                        onClick={() => handlePick(t.name)}
                                        disabled={disabled}
                                        className="min-w-0 flex-1 px-2 py-1.5 text-left disabled:opacity-40"
                                        title={t.name}
                                    >
                                        <span className="block truncate text-sm text-slate-700">
                                            {t.name}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(t.id)}
                                        disabled={busy}
                                        className="shrink-0 rounded p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-500 disabled:opacity-40"
                                        title="Vorlage löschen"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                    <div className="border-t border-slate-100 p-1.5">
                        <button
                            type="button"
                            onClick={handleSaveCurrent}
                            disabled={busy || disabled || !currentLabel.trim()}
                            className="flex w-full items-center gap-2 rounded px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
                            title={
                                currentLabel.trim()
                                    ? 'Aktuelle Bezeichnung als Vorlage speichern'
                                    : 'Bezeichnung ist leer'
                            }
                        >
                            {busy ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <FilePlus className="h-3.5 w-3.5" />
                            )}
                            Als Vorlage speichern
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
